import React, { useEffect, useState } from 'react';
import axios, { decryptData } from '../Axiosinstance';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography
} from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../Loader';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Button } from '@mui/material';
import { Chip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';


const MarkUpdation = ({ intakeId }) => {
  const [students, setStudents] = useState([]);
  const [modules, setModules] = useState([]);
  const [editedMarks, setEditedMarks] = useState({});
  const intakeId1 = decryptData(decodeURIComponent(intakeId));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get(`/registrationadvances/intake/${intakeId1}`);
        setStudents(res.data);
        if (res.data.length > 0) {
          const allModules = res.data[0].markSheets.map(m => m.module);
          setModules(allModules);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }

    };
    fetchData();
  }, [intakeId]);

  const handleInputChange = (studentId, module, value) => {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed > 100) {
      toast.warn("Mark cannot exceed 100");
      return;
    }

    setEditedMarks(prev => ({
      ...prev,
      [`${studentId}_${module}`]: value
    }));
  };


  const handleBlur = async (studentId, registrationId, module) => {
    const key = `${studentId}_${module}`;
    const value = editedMarks[key];
    if (value === undefined) return;

    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) return;

    if (parsed > 100) {
      toast.error("Mark must be less than or equal to 100");
      return;
    }

    const student = students.find(s => s.studentId === studentId);
    const studentName = student?.studentName || `ID ${studentId}`;

    try {
      await axios.put(`/registrationadvances/registration/${registrationId}/marks`, {
        module,
        mark: parsed,
      });

      toast.success(`Updated "${module}" mark for ${studentName}`);

      setStudents(prev =>
        prev.map(s =>
          s.studentId === studentId
            ? {
              ...s,
              markSheets: s.markSheets.map(ms =>
                ms.module === module ? { ...ms, mark: parsed } : ms
              )
            }
            : s
        )
      );
    } catch (err) {
      toast.error(`Failed to update "${module}" mark for ${studentName}`);
      console.error('Error updating mark:', err);
    }

    setEditedMarks(prev => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader />
      </Box>
    );
  }

  return (


    <Box sx={{
      p: { xs: 1, sm: 2, md: 4 },
      backgroundColor: 'background.paper',
      borderRadius: 2,
      boxShadow: 1,
      marginTop: '10px',
      width: '100%',
    }}>


      <Box sx={{
        overflowX: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <TableContainer component={Paper} sx={{
          width: '100%',
          minWidth: '650px',
          maxHeight: '70vh',
          '&::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 4 }
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="right" sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 2, // higher than module headers to layer correctly
                  bgcolor: 'background.paper', // prevent overlap issues
                  fontWeight: 500,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  whiteSpace: 'nowrap'
                }}>
                  Student Name
                </TableCell>
                {modules.map((module, index) => (
                  <TableCell key={index} align="center" sx={{

                    fontWeight: 700,
                    bgcolor: '#e9e9e9',
                    color: 'Black',
                    fontSize: '0.9rem',
                    py: 2,
                    whiteSpace: 'wrap',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}>
                    {module}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {students.map((student) => (
                <TableRow
                  key={student.studentId}
                  sx={{
                    '&:nth-of-type(even)': { bgcolor: 'action.hover' },
                    '&:hover': { bgcolor: 'primary.lightest' }
                  }}
                >
                  <TableCell align="right" sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2, // higher than module headers to layer correctly
                    bgcolor: 'background.paper', // prevent overlap issues
                    fontWeight: 500,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    whiteSpace: 'nowrap'
                  }}>
                    {student.studentName}
                  </TableCell>

                  {modules.map((module, index) => {
                    const markObj = student.markSheets.find(m => m.module === module);
                    const inputKey = `${student.studentId}_${module}`;
                    const value =
                      editedMarks[inputKey] !== undefined
                        ? editedMarks[inputKey]
                        : markObj?.mark === -1
                          ? ''
                          : markObj?.mark;

                    return (
                      <TableCell key={index} align="center" sx={{ py: 1, px: 0.5 }}>
                        <TextField
                          type="number"
                          value={value}
                          onChange={(e) =>
                            handleInputChange(
                              student.studentId,
                              module,
                              e.target.value
                            )
                          }
                          onBlur={() =>
                            handleBlur(
                              student.studentId,
                              student.registrationId,
                              module
                            )
                          }
                          inputProps={{
                            min: 0,
                            max: 100,
                            style: { textAlign: 'center' }
                          }}
                          size="small"
                          fullWidth
                          sx={{
                            width: 70,
                            '& .MuiInputBase-root': {
                              bgcolor: editedMarks ? 'warning.lightest' : 'background.paper',
                              borderRadius: 1,
                              border: '1px solid',
                              // borderColor: editedMarks ? 'warning.main' : 'divider',
                              transition: 'all 0.2s'
                            },
                            '& .MuiInputBase-input': { py: 1 }
                          }}
                          variant="outlined"
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <ToastContainer position="bottom-right" />
    </Box>

  );
};

export default MarkUpdation;
